import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  Search, ChevronRight, Play, Eye, ArrowUpDown, Crown, Monitor, Wifi, Shield,
  Newspaper, Clapperboard, Church, Trophy, Music, Baby, GraduationCap, HeartPulse,
  Briefcase, Film, Camera, Laugh, FlaskConical, Cpu, Gamepad2, Utensils, Plane,
  Heart, Shirt, Tv, ChevronLeft, Loader2
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";

const ICON_MAP = {
  newspaper: Newspaper, clapperboard: Clapperboard, church: Church, trophy: Trophy,
  music: Music, baby: Baby, 'graduation-cap': GraduationCap, 'heart-pulse': HeartPulse,
  briefcase: Briefcase, film: Film, camera: Camera, laugh: Laugh, 'flask-conical': FlaskConical,
  cpu: Cpu, 'gamepad-2': Gamepad2, utensils: Utensils, plane: Plane, heart: Heart,
  shirt: Shirt, tv: Tv,
};

function ChannelCard({ channel, idx }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: idx * 0.03, duration: 0.4 }}
    >
      <Link
        to={`/watch/${channel.id}`}
        data-testid={`channel-card-${channel.id}`}
        className="group block rounded-2xl overflow-hidden border border-white/[0.06] bg-[#0F172A] hover:border-[#FFC928]/30 hover:shadow-[0_8px_30px_rgba(255,201,40,0.1)] transition-all duration-300"
      >
        <div className="aspect-video relative bg-[#050814] overflow-hidden">
          <img
            src={channel.logo_url || "https://picsum.photos/seed/ch/400/300"}
            alt={channel.name}
            className="w-full h-full object-contain p-4 group-hover:scale-110 transition-transform duration-500"
            referrerPolicy="no-referrer"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-transparent to-transparent opacity-60" />
          {/* LIVE badge */}
          <div className="absolute top-2.5 left-2.5 flex items-center gap-1 px-2 py-0.5 rounded-lg bg-red-500/90 backdrop-blur-sm">
            <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
            <span className="text-[10px] font-bold text-white uppercase tracking-wider">Live</span>
          </div>
          {channel.is_free === 0 && (
            <div className="absolute top-2.5 right-2.5 px-1.5 py-0.5 rounded bg-[#FFC928]/90 text-[10px] font-bold text-black">
              HD
            </div>
          )}
          {/* Hover play */}
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <div className="w-12 h-12 bg-[#FFC928] rounded-full flex items-center justify-center shadow-[0_0_25px_rgba(255,201,40,0.4)]">
              <Play size={20} className="text-black fill-black ml-0.5" />
            </div>
          </div>
        </div>
        <div className="p-3">
          <h4 className="font-semibold text-sm truncate text-white mb-1 group-hover:text-[#FFC928] transition-colors">{channel.name}</h4>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              {channel.country_flag && (
                <img src={channel.country_flag} alt="" className="w-4 h-3 object-cover rounded-sm" />
              )}
              <span className="text-[10px] text-[#94A3B8]">{channel.country_name}</span>
            </div>
            <div className="flex items-center gap-1 text-[10px] text-[#94A3B8]">
              <Eye size={10} />
              <span>{channel.views || 0}</span>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export default function CategoryDetail() {
  const { slug } = useParams();
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'fr';

  const [category, setCategory] = useState(null);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [channelsLoading, setChannelsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/categories/slug/${slug}?lang=${lang}`)
      .then(res => { setCategory(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug, lang]);

  useEffect(() => {
    setChannelsLoading(true);
    axios.get(`${API}/categories/slug/${slug}/channels`, {
      params: { page, limit: 24, search, sort: sortBy }
    })
      .then(res => {
        setChannels(res.data.channels || []);
        setTotalPages(res.data.pages || 1);
        setTotal(res.data.total || 0);
        setChannelsLoading(false);
      })
      .catch(() => setChannelsLoading(false));
  }, [slug, page, search, sortBy]);

  const sortLabels = {
    name: lang === 'fr' ? 'Nom A–Z' : 'Name A–Z',
    name_desc: lang === 'fr' ? 'Nom Z–A' : 'Name Z–A',
    views: lang === 'fr' ? 'Plus populaires' : 'Most Popular',
    recent: lang === 'fr' ? 'Ajoutées récemment' : 'Recently Added',
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050814] flex items-center justify-center">
        <Loader2 size={32} className="text-[#FFC928] animate-spin" />
      </div>
    );
  }

  if (!category) {
    return (
      <div className="min-h-screen bg-[#050814] flex flex-col items-center justify-center text-center px-4">
        <Tv size={48} className="text-[#94A3B8]/30 mb-4" />
        <h2 className="text-xl font-bold text-white mb-2">{lang === 'fr' ? 'Catégorie introuvable' : 'Category not found'}</h2>
        <p className="text-[#94A3B8] text-sm mb-6">{lang === 'fr' ? 'Cette catégorie n\'existe pas.' : 'This category does not exist.'}</p>
        <Link to="/categories" className="px-6 py-2 rounded-xl bg-[#FFC928] text-black font-bold text-sm hover:bg-[#FFD54F] transition-colors">
          {lang === 'fr' ? 'Voir les catégories' : 'Browse Categories'}
        </Link>
      </div>
    );
  }

  const IconComp = ICON_MAP[category.icon] || Tv;

  return (
    <div className="min-h-screen bg-[#050814]" data-testid="category-detail-page">
      {/* Hero */}
      <div className="relative overflow-hidden">
        {category.image_url && (
          <img
            src={category.image_url}
            alt={category.name}
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-[#050814]/70 via-[#050814]/85 to-[#050814]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050814]/90 via-transparent to-[#050814]/90" />

        <div className="relative px-4 sm:px-6 lg:px-8 pt-8 pb-10">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-[#94A3B8] mb-6" data-testid="category-breadcrumb">
            <Link to="/" className="hover:text-white transition-colors">{lang === 'fr' ? 'Accueil' : 'Home'}</Link>
            <ChevronRight size={14} />
            <Link to="/categories" className="hover:text-white transition-colors">{lang === 'fr' ? 'Catégories' : 'Categories'}</Link>
            <ChevronRight size={14} />
            <span className="text-[#FFC928] font-medium">{category.name}</span>
          </nav>

          <div className="flex items-start gap-4">
            <div
              className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 border border-white/10"
              style={{ background: `${category.accent_color}22` }}
            >
              <IconComp size={24} style={{ color: category.accent_color }} />
            </div>
            <div>
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2"
                style={{ fontFamily: 'Poppins' }}
                data-testid="category-detail-title"
              >
                {lang === 'fr' ? `Chaînes ${category.name_fr || category.name}` : `${category.name_en || category.name} Channels`}
              </motion.h1>
              <p className="text-[#94A3B8] text-sm max-w-2xl">{category.description}</p>
              <p className="text-xs mt-2" style={{ color: category.accent_color }}>
                {total} {lang === 'fr' ? 'chaînes actives' : 'active channels'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 border-b border-white/[0.06]">
        <div className="relative flex-1 max-w-md w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" size={16} />
          <input
            type="text"
            data-testid="category-channels-search"
            placeholder={lang === 'fr' ? 'Rechercher dans cette catégorie...' : 'Search in this category...'}
            className="w-full pl-10 pr-4 py-2 rounded-lg bg-white/[0.05] border border-white/[0.06] text-white text-sm placeholder-[#94A3B8]/60 focus:outline-none focus:ring-1 focus:ring-[#FFC928]/30"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
        <div className="relative">
          <select
            value={sortBy}
            onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
            data-testid="category-channels-sort"
            className="appearance-none pl-8 pr-8 py-2 rounded-lg bg-white/[0.05] border border-white/[0.06] text-[#94A3B8] text-sm focus:outline-none cursor-pointer"
          >
            {Object.entries(sortLabels).map(([k, v]) => (
              <option key={k} value={k} className="bg-[#0F172A] text-white">{v}</option>
            ))}
          </select>
          <ArrowUpDown size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none" />
        </div>
      </div>

      {/* Channels Grid */}
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        {channelsLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="rounded-2xl bg-[#0F172A] border border-white/[0.06] overflow-hidden animate-pulse">
                <div className="aspect-video bg-white/[0.03]" />
                <div className="p-3 space-y-2">
                  <div className="h-3 bg-white/[0.05] rounded w-3/4" />
                  <div className="h-2 bg-white/[0.03] rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : channels.length === 0 ? (
          <div className="text-center py-20" data-testid="channels-empty">
            <Tv size={48} className="mx-auto text-[#94A3B8]/30 mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">
              {lang === 'fr' ? 'Aucune chaîne dans cette catégorie' : 'No channels in this category'}
            </h3>
            <p className="text-[#94A3B8] text-sm">
              {lang === 'fr' ? 'Les chaînes seront ajoutées prochainement.' : 'Channels will be added soon.'}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="channels-grid">
              {channels.map((ch, idx) => (
                <ChannelCard key={ch.id} channel={ch} idx={idx} />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 mt-10" data-testid="channels-pagination">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="flex items-center gap-1 px-4 py-2 rounded-lg bg-white/[0.05] border border-white/[0.06] text-[#94A3B8] text-sm disabled:opacity-40 hover:bg-white/[0.08] hover:text-white transition-colors"
                >
                  <ChevronLeft size={16} /> {lang === 'fr' ? 'Précédent' : 'Previous'}
                </button>
                <span className="text-sm text-[#94A3B8]">
                  {page} / {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="flex items-center gap-1 px-4 py-2 rounded-lg bg-white/[0.05] border border-white/[0.06] text-[#94A3B8] text-sm disabled:opacity-40 hover:bg-white/[0.08] hover:text-white transition-colors"
                >
                  {lang === 'fr' ? 'Suivant' : 'Next'} <ChevronRight size={16} />
                </button>
              </div>
            )}
          </>
        )}

        {/* Related Categories */}
        {category.related_categories && category.related_categories.length > 0 && (
          <div className="mt-16">
            <h2 className="text-xl font-bold text-white mb-6" style={{ fontFamily: 'Poppins' }}>
              {lang === 'fr' ? 'Catégories similaires' : 'Related Categories'}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {category.related_categories.map((rel) => {
                const RelIcon = ICON_MAP[rel.icon] || Tv;
                return (
                  <Link
                    key={rel.id}
                    to={`/categories/${rel.slug}`}
                    data-testid={`related-cat-${rel.slug}`}
                    className="group rounded-xl border border-white/[0.06] bg-[#0F172A] p-4 text-center hover:border-[#FFC928]/30 transition-all"
                  >
                    <div className="w-10 h-10 rounded-lg mx-auto mb-2 flex items-center justify-center" style={{ background: `${rel.accent_color}22` }}>
                      <RelIcon size={18} style={{ color: rel.accent_color }} />
                    </div>
                    <p className="text-xs font-medium text-white group-hover:text-[#FFC928] transition-colors truncate">{rel.name}</p>
                    <p className="text-[10px] text-[#94A3B8] mt-0.5">{rel.channel_count} ch</p>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Premium Banner */}
        <div className="mt-16 relative rounded-2xl overflow-hidden border border-[#FFC928]/20">
          <div className="absolute inset-0 bg-gradient-to-r from-[#FFC928]/10 via-[#0F172A] to-[#FFC928]/10" />
          <div className="relative px-6 py-10 text-center">
            <Crown size={32} className="mx-auto text-[#FFC928] mb-3" />
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Poppins' }}>
              {lang === 'fr' ? "Vivez l'expérience Djilu Premium" : 'Experience Djilu Premium'}
            </h2>
            <p className="text-[#94A3B8] text-sm mb-5 flex flex-wrap items-center justify-center gap-2">
              <span>{lang === 'fr' ? 'Plus de 100 chaînes' : '100+ channels'}</span> •
              <span>{lang === 'fr' ? 'Qualité HD' : 'HD quality'}</span> •
              <span>{lang === 'fr' ? 'Sans publicité' : 'Ad-free'}</span> •
              <span>{lang === 'fr' ? 'Regardez partout' : 'Watch anywhere'}</span>
            </p>
            <Link
              to="/membership"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[#FFC928] text-black font-bold text-sm hover:bg-[#FFD54F] transition-all shadow-[0_0_25px_rgba(255,201,40,0.3)]"
            >
              <Crown size={16} /> {lang === 'fr' ? 'Devenir Premium' : 'Become Premium'}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
